import * as React from "react";
import {
  Box,
  Button,
  Chip,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";

function CopyButton({ text, label }) {
  const [copied, setCopied] = React.useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <Tooltip title={copied ? "Copied!" : `Copy ${label}`}>
      <Button
        size="small"
        variant="outlined"
        startIcon={copied ? <CheckCircleOutlineIcon /> : <ContentCopyIcon />}
        onClick={handleCopy}
        sx={{ minWidth: 90 }}
      >
        {copied ? "Copied!" : `Copy ${label}`}
      </Button>
    </Tooltip>
  );
}

/**
 * Props:
 *   participant: {
 *     firstName: string,
 *     lastName: string,
 *     email: string,
 *     phone?: string | null,
 *     hasOutstandingPayments: boolean,
 *   }
 */
export default function ParticipantCard({ participant }) {
  const { firstName, lastName, email, phone, hasOutstandingPayments } = participant ?? {};
  const fullName = [firstName, lastName].filter(Boolean).join(" ") || "Unknown";

  return (
    <Box sx={{ py: 1 }}>
      <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" sx={{ mb: 1 }}>
        <Typography variant="subtitle1" fontWeight={600}>
          {fullName}
        </Typography>
        {hasOutstandingPayments ? (
          <Chip label="Outstanding payments" size="small" color="warning" variant="outlined" />
        ) : (
          <Chip label="Paid up" size="small" color="success" variant="outlined" />
        )}
      </Stack>

      <Stack spacing={1.5} sx={{ mt: 1 }}>
        {email && (
          <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" gap={1}>
            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ minWidth: 0, flex: 1 }}>
              <EmailIcon fontSize="small" color="action" sx={{ flexShrink: 0 }} />
              <Typography
                variant="body2"
                component="a"
                href={`mailto:${email}`}
                sx={{ color: "primary.main", textDecoration: "none", wordBreak: "break-all", "&:hover": { textDecoration: "underline" } }}
              >
                {email}
              </Typography>
            </Stack>
            <CopyButton text={email} label="email" />
          </Stack>
        )}

        {phone ? (
          <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" gap={1}>
            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ minWidth: 0, flex: 1 }}>
              <PhoneIcon fontSize="small" color="action" sx={{ flexShrink: 0 }} />
              <Typography
                variant="body2"
                component="a"
                href={`tel:${phone}`}
                sx={{ color: "primary.main", textDecoration: "none", "&:hover": { textDecoration: "underline" } }}
              >
                {phone}
              </Typography>
            </Stack>
            <CopyButton text={phone} label="phone" />
          </Stack>
        ) : (
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <PhoneIcon fontSize="small" color="disabled" sx={{ flexShrink: 0 }} />
            <Typography variant="body2" color="text.disabled">
              No phone on file
            </Typography>
          </Stack>
        )}
      </Stack>
    </Box>
  );
}
